import React from 'react';
import "../style/AppPages.css";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from 'react-responsive-carousel';
import { Trans, useTranslation } from 'react-i18next';
import useViewport from '../hooks/useViewport';

import gifExtraSoft from '../assets/softness/ExtraSoft.gif';
import gifExtraSoftHandling from '../assets/softness/ExtraSoftHandling.gif';
import gifSoft from '../assets/softness/Soft.gif';
import gifSoftHandling from '../assets/softness/SoftHandling.gif';
import gifMedium from '../assets/softness/Medium.gif';
import gifMediumHandling from '../assets/softness/MediumHandling.gif';
import gifFirm from '../assets/softness/Firm.gif';
import gifFirmHandling from '../assets/softness/FirmHandling.gif';

const LEVELS = [
    {
        key: 'xsoft',
        shore: '00-20',
        gifs: [gifExtraSoft, gifExtraSoftHandling],
        titleKey:   'softnessGuide.softnessTextXSoftTitle',
        descKey:    'softnessGuide.softnessTextXSoft',
        bestForKey: 'softnessGuide.softnessTextXSoftBestFor',
        accent: '#E6716A',
    },
    {
        key: 'soft',
        shore: '00-30',
        gifs: [gifSoft, gifSoftHandling],
        titleKey:   'softnessGuide.softnessTextSoftTitle',
        descKey:    'softnessGuide.softnessTextSoft',
        bestForKey: 'softnessGuide.softnessTextSoftBestFor',
        accent: '#d98ac6',
    },
    {
        key: 'medium',
        shore: '00-50',
        gifs: [gifMedium, gifMediumHandling],
        titleKey:   'softnessGuide.softnessTextMediumTitle',
        descKey:    'softnessGuide.softnessTextMedium',
        bestForKey: 'softnessGuide.softnessTextMediumBestFor',
        accent: '#ad6fc9',
    },
    {
        key: 'firm',
        shore: '00-70',
        gifs: [gifFirm, gifFirmHandling],
        titleKey:   'softnessGuide.softnessTextFirmTitle',
        descKey:    'softnessGuide.softnessTextFirm',
        bestForKey: 'softnessGuide.softnessTextFirmBestFor',
        accent: '#8254bd',
    },
];

const SoftnessScale = ({ activeIndex, t, onNodeClick }) => (
    <div className="softness-scale">
        <div className="softness-scale__line" />
        <div className="softness-scale__nodes">
            {LEVELS.map((lvl, i) => {
                const active = i === activeIndex;
                return (
                    <div
                        key={lvl.key}
                        className={`softness-scale__node${active ? ' is-active' : ''}`}
                        onClick={active ? undefined : () => onNodeClick(i)}
                        role={active ? undefined : 'button'}
                        tabIndex={active ? undefined : 0}
                        onKeyDown={active ? undefined : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onNodeClick(i);
                            }
                        }}
                        style={{ cursor: active ? 'default' : 'pointer' }}
                    >
                        <span className="softness-scale__dot-wrap">
                            <span
                                className="softness-scale__dot"
                                style={{ background: lvl.accent, boxShadow: active ? `0 0 0 3px ${lvl.accent}` : 'none' }}
                            />
                        </span>
                        <span className="softness-scale__label">{t(lvl.titleKey)}</span>
                    </div>
                );
            })}
        </div>
    </div>
);

const SoftnessGuide = () => {
    const { t } = useTranslation();
    const { isTinyMobileView } = useViewport();
    const cardRefs = React.useRef([]);

    const scrollToLevel = (index) => {
        const el = cardRefs.current[index];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="page-padding-info">
            <div className="text-page-container">
                <h1 className="text-page-title">{t("softnessGuide.title")}</h1>

                <p>&nbsp;{t("softnessGuide.softnessIntro")}</p>

                <div className="softness-levels">
                    {LEVELS.map((level, i) => (
                        <div
                            key={level.key}
                            ref={(el) => (cardRefs.current[i] = el)}
                            className={`softness-card softness-card--${i % 2 === 0 ? 'left' : 'right'}`}
                        >
                            <div className="softness-card__media">
                                <Carousel
                                    autoPlay
                                    interval={14000}
                                    infiniteLoop
                                    showThumbs={false}
                                    showStatus={false}
                                    stopOnHover={false}
                                    className="softness-carousel"
                                    renderArrowPrev={(onClick) => (
                                        <button className="softness-carousel__btn softness-carousel__btn--prev" onClick={onClick} aria-label="Previous">&#8249;</button>
                                    )}
                                    renderArrowNext={(onClick) => (
                                        <button className="softness-carousel__btn softness-carousel__btn--next" onClick={onClick} aria-label="Next">&#8250;</button>
                                    )}
                                >
                                    {level.gifs.map((gif, gi) => (
                                        <img
                                            key={gi}
                                            className="softness-card__gif"
                                            src={gif}
                                            alt={t(level.titleKey)}
                                            loading="lazy"
                                        />
                                    ))}
                                </Carousel>
                            </div>

                            <div className="softness-card__body">

                                {isTinyMobileView ? null : (
                                    <SoftnessScale activeIndex={i} t={t} onNodeClick={scrollToLevel} />
                                )}

                                <div className="softness-card__header">
                                    <h2 className="softness-card__title">{t(level.titleKey)}</h2>
                                    <span className="softness-chip">SHORE {level.shore}</span>
                                </div>
                                <p className="softness-card__desc">{t(level.descKey)}</p>
                                <div className="softness-card__bestfor">
                                    <span className="softness-card__bestfor-label">
                                        {t("softnessGuide.softnessTextBestForTitle")}
                                    </span>
                                    <span>{t(level.bestForKey)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <br />
                <ul>
                    <li>
                        <p>
                            <strong>{t("softnessGuide.softnessTextSplitTitle")} :</strong>
                            &nbsp;{t("softnessGuide.softnessTextSplit")}
                        </p>
                    </li>
                    <li>
                        <p>
                            <strong>{t("softnessGuide.softnessTextLimitationsTitle")} :</strong>
                            &nbsp;{t("softnessGuide.softnessTextLimitations")}
                        </p>
                    </li>
                    <li>
                        <p>
                            <strong>{t("softnessGuide.softnessTextSampleTitle")} :</strong>
                            &nbsp;
                            <Trans
                                i18nKey="softnessGuide.softnessTextSample"
                                components={{ 1: <strong />, 2: <a href="/product/softness-sample-pucks" className="no-link-color" /> }}
                            />
                        </p>
                    </li>
                </ul>

                <br />
                <p>
                    &nbsp;
                    <Trans i18nKey="softnessGuide.softnessTextContact" components={{ 1: <a href="mailto:contact@awootoys.com" /> }} />
                    <br /><br />
                    &nbsp;{t("softnessGuide.softnessTextBottom")}
                </p>
            </div>
        </div>
    );
};

export default SoftnessGuide;